import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaSave, FaUndo, FaPen, FaTimes } from "react-icons/fa";
import { handleTokenAndPostRequest } from "../utils/utils";

const SMS_PART_COST_CENTS = 20;

const CAMPAIGNS = [
  {
    key: "new_data_available_sms",
    label: "Suburb Update SMS",
    description:
      "Sent to mobile-only contacts when new suburb-level sold data is available. The intro version goes the first time; subsequent quarterly sends use the follow-up.",
  },
  {
    key: "property_update_sms",
    label: "Property Update SMS",
    description:
      "Sent alongside the property update email when a contact has a mobile. Intro is the first SMS they ever receive from this campaign; subsequent sends use the follow-up.",
  },
];

const VARIANTS = [
  { key: "intro", label: "Intro (first ever send)" },
  { key: "update", label: "Follow-up (subsequent sends)" },
];

// Mirror jobs/sms_template_defaults.render_template: replace {token} with the
// supplied value, and when the value is empty also eat one piece of adjacent
// glue (", " or " ") so "Hi {contact_first_name}, X" degrades to "Hi X".
function renderTemplate(template, variables) {
  if (!template) return "";
  let out = template;
  for (const [k, v] of Object.entries(variables)) {
    const token = "{" + k + "}";
    const isEmpty = v == null || (typeof v === "string" && !v.trim());
    if (isEmpty) {
      out = out
        .split(token + ", ").join("")
        .split(", " + token).join("")
        .split(token + " ").join("")
        .split(" " + token).join("")
        .split(token).join("");
    } else {
      out = out.split(token).join(String(v));
    }
  }
  return out;
}

// Sample values used to render the live preview.
const BASE_PREVIEW_VARS = {
  contact_first_name: "Jane",
  agent_name: "Alex Smith",
  agency_name_short: "Atlas Byron Bay",
  address: "Smith St",
  month: new Date().toLocaleString("en-AU", { month: "long" }),
  search_link: "https://homeprice.site/psl/?t=ab12cd34ef",
  property_link: "https://homeprice.site/p/123?t=ab12cd34ef",
  notif_link: "https://homeprice.site/n/?t=ab12cd34ef",
};

function buildPreviewVars(agencyNameShort, agentName) {
  const out = { ...BASE_PREVIEW_VARS };
  const agency = (agencyNameShort || "").trim();
  if (agency) out.agency_name_short = agency;
  const agent = (agentName || "").trim();
  if (agent) out.agent_name = agent;
  return out;
}

// SMS segmentation matches backend _sms_part_count (GSM-7 vs UCS-2).
const GSM_BASIC = new Set(
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà".split(""),
);
const GSM_EXT = new Set("\f^{}\\[~]|€".split(""));

function segmentInfo(body) {
  if (!body) return { chars: 0, parts: 1, encoding: "GSM-7" };
  const chars = Array.from(body);
  const isGsm = chars.every((c) => GSM_BASIC.has(c) || GSM_EXT.has(c));
  if (isGsm) {
    const weight = chars.reduce((acc, c) => acc + (GSM_EXT.has(c) ? 2 : 1), 0);
    const parts = weight <= 160 ? 1 : Math.ceil((weight - 160) / 153) + 1;
    return { chars: chars.length, parts, encoding: "GSM-7" };
  }
  const n = chars.length;
  const parts = n <= 70 ? 1 : Math.ceil((n - 70) / 67) + 1;
  return { chars: n, parts, encoding: "UCS-2 (Unicode)" };
}

const hasUnsub = (t) => /\bstop\b/i.test(t || "");
const hasNotifLink = (t) => /\{notif_link\}/.test(t || "");

// --- Modal: full editor ------------------------------------------------------

// Single-template modal. campaignKey + variantKey scope the editor to one of
// the four messages so the user is always looking at exactly what their Edit
// button opened — no scrolling around a multi-section dialog.
function EditModal({ campaignKey, variantKey, initialValue, defaults, variables, saved, previewVars, onClose, onSaved }) {
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const textareaRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const campaign = CAMPAIGNS.find((c) => c.key === campaignKey);
  const variant = VARIANTS.find((v) => v.key === variantKey);
  const defaultValue = defaults?.[campaignKey]?.[variantKey] || "";
  const isCustomised = saved?.[campaignKey]?.[variantKey];
  const isDefault = (value || "").trim() === defaultValue.trim();
  const dirty = (value || "") !== (initialValue || "");
  const preview = renderTemplate(value, previewVars);
  const previewSeg = segmentInfo(preview);
  const previewCost = previewSeg.parts * SMS_PART_COST_CENTS;
  const showUnsubWarning = !hasUnsub(value) && !hasNotifLink(value);

  const insertVariable = (varName) => {
    const token = "{" + varName + "}";
    const ref = textareaRef.current;
    if (ref) {
      const start = ref.selectionStart ?? value.length;
      const end = ref.selectionEnd ?? value.length;
      const next = value.slice(0, start) + token + value.slice(end);
      setValue(next);
      setMsg("");
      requestAnimationFrame(() => {
        ref.focus();
        const pos = start + token.length;
        ref.setSelectionRange(pos, pos);
      });
    } else {
      setValue(value + (!value || value.endsWith(" ") ? "" : " ") + token);
      setMsg("");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg("");
    try {
      const trimmed = (value || "").trim();
      const payloadValue = trimmed && trimmed !== defaultValue.trim() ? trimmed : null;
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/save-campaign-sms-templates`,
        { templates: { [campaignKey]: { [variantKey]: payloadValue } } },
      );
      if (res.data?.status === "success") {
        onSaved(campaignKey, variantKey, value, payloadValue !== null);
      } else {
        setMsg(res.data?.message || "Failed to save template.");
      }
    } catch (e) {
      console.error("Failed to save campaign SMS template:", e);
      setMsg("Failed to save template. Please try again.");
    }
    setSaving(false);
  };

  // The left nav is 260px wide on desktop. Anchor the overlay to the right of
  // it so the dialog centres inside the visible pane; on mobile (or when the
  // collapsed nav covers the whole viewport) fall back to full-screen.
  const overlayStyle = isDesktop
    ? { position: "fixed", top: 0, right: 0, bottom: 0, left: 260, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }
    : { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "stretch", justifyContent: "stretch", padding: 0 };
  const dialogStyle = isDesktop
    ? { background: "#fff", borderRadius: 14, maxWidth: 620, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 24, position: "relative", boxShadow: "0 8px 32px rgba(0,0,0,0.22)" }
    : { background: "#fff", borderRadius: 0, width: "100%", height: "100%", overflowY: "auto", padding: 20, position: "relative" };

  return (
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={dialogStyle}>
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 12, right: 12, background: "none",
            border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 16, padding: 4,
          }}
          aria-label="Close"
        >
          <FaTimes />
        </button>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6 }}>
          {campaign?.label}
        </div>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: "#1f2937", margin: "2px 0 4px" }}>
          {variant?.label}
        </h3>
        <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.5 }}>
          Each SMS part costs $0.20. Emoji and curly quotes switch the message to Unicode (UCS-2), which halves how much fits per part.
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: "#6b7280", alignSelf: "center", marginRight: 4 }}>
            Insert variable:
          </span>
          {(variables[campaignKey] || []).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => insertVariable(v)}
              style={{
                fontSize: 11, padding: "3px 8px", borderRadius: 999,
                border: "1px solid #d1d5db", background: "#f9fafb",
                color: "#374151", cursor: "pointer", fontFamily: "ui-monospace, monospace",
              }}
            >
              {`{${v}}`}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
            Template
            {isCustomised && !isDefault && (
              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 500, color: "#0a9fa6", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Customised
              </span>
            )}
          </span>
          {!isDefault && (
            <button
              type="button"
              onClick={() => { setValue(defaultValue); setMsg(""); }}
              style={{
                fontSize: 11, padding: "3px 8px", borderRadius: 6,
                border: "1px solid #d1d5db", background: "#fff", color: "#374151",
                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
              }}
            >
              <FaUndo size={9} /> Restore default
            </button>
          )}
        </div>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => { setValue(e.target.value); setMsg(""); }}
          rows={5}
          style={{
            width: "100%", padding: "10px 12px", borderRadius: 8,
            border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit",
            boxSizing: "border-box", lineHeight: 1.5, resize: "vertical",
          }}
        />
        <div style={{ marginTop: 4, fontSize: 11, color: "#6b7280" }}>
          <strong style={{ color: "#374151" }}>{previewSeg.chars}</strong> chars,{" "}
          <strong style={{ color: "#374151" }}>{previewSeg.parts}</strong> SMS part{previewSeg.parts === 1 ? "" : "s"} ·{" "}
          <strong style={{ color: previewCost > SMS_PART_COST_CENTS ? "#b45309" : "#059669" }}>
            ${(previewCost / 100).toFixed(2)}
          </strong>{" "}
          per send · {previewSeg.encoding}
        </div>
        {showUnsubWarning && (
          <div style={{ marginTop: 6, fontSize: 11, color: "#b45309", lineHeight: 1.4 }}>
            Heads up: this template has no <code>STOP</code> instruction and no <code>{"{notif_link}"}</code>.
            Australian SMS marketing rules require a way to opt out — include either an unsubscribe instruction
            or the notification link.
          </div>
        )}

        <div style={{ marginTop: 12, padding: "10px 12px", background: "#f9fafb", border: "1px dashed #e5e7eb", borderRadius: 8, fontSize: 12.5, color: "#374151", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
          <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Preview</div>
          {preview || <span style={{ color: "#9ca3af" }}>(empty)</span>}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          {msg && (
            <span style={{
              fontSize: 13, fontWeight: 500, marginRight: "auto",
              color: msg.toLowerCase().includes("saved") ? "#10b981" : "#e53935",
            }}>
              {msg}
            </span>
          )}
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px", borderRadius: 8, border: "1px solid #d1d5db",
              background: "#fff", color: "#374151", fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            style={{
              padding: "8px 20px", borderRadius: 8, border: "none",
              background: dirty ? "linear-gradient(90deg, #1a3f6b, #10b981)" : "#d1d5db",
              color: "#fff", fontWeight: 600, fontSize: 14,
              cursor: dirty ? "pointer" : "default",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <FaSave size={11} /> {saving ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Top-level: preview cards + Edit button ---------------------------------

// When `campaignKey` is set, only that campaign's previews are rendered (the
// component is now used per-section in Settings → Campaigns). Omitting the
// prop falls back to the previous "all campaigns at once" layout.
export default function CampaignSmsTemplatesEditor({ campaignKey, agencyNameShort, agentName, onFollowUpPartsChange } = {}) {
  const previewVars = useMemo(() => buildPreviewVars(agencyNameShort, agentName), [agencyNameShort, agentName]);
  const [defaults, setDefaults] = useState({});
  const [variables, setVariables] = useState({});
  // values holds the persisted state (saved overrides; defaults otherwise). The
  // modal makes a working copy so cancelling discards mid-edit changes.
  const [values, setValues] = useState({});
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // Which (campaign, variant) the editor is open for. null = closed.
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/get-campaign-sms-templates`,
        {},
      );
      const d = res.data || {};
      setDefaults(d.defaults || {});
      setVariables(d.variables || {});
      const initialValues = {};
      const initialSaved = {};
      for (const c of CAMPAIGNS) {
        initialValues[c.key] = {};
        initialSaved[c.key] = {};
        for (const v of VARIANTS) {
          const savedVal = d.saved?.[c.key]?.[v.key];
          const defaultVal = d.defaults?.[c.key]?.[v.key] || "";
          initialValues[c.key][v.key] = savedVal || defaultVal;
          initialSaved[c.key][v.key] = !!savedVal;
        }
      }
      setValues(initialValues);
      setSaved(initialSaved);
    } catch (e) {
      console.error("Failed to load campaign SMS templates:", e);
      setLoadError("Failed to load templates. Refresh to try again.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!onFollowUpPartsChange || !campaignKey) return;
    const tpl = values[campaignKey]?.update || "";
    const preview = renderTemplate(tpl, previewVars);
    onFollowUpPartsChange(segmentInfo(preview).parts);
  }, [values, campaignKey, previewVars, onFollowUpPartsChange]);

  const handleSaved = (ck, vk, newValue, isCustomised) => {
    setValues((prev) => ({ ...prev, [ck]: { ...(prev[ck] || {}), [vk]: newValue } }));
    setSaved((prev) => ({ ...prev, [ck]: { ...(prev[ck] || {}), [vk]: isCustomised } }));
    setEditing(null);
  };

  if (loading) {
    return (
      <div style={{ marginTop: 22, paddingTop: 18, borderTop: "1px solid #f0f0f0", fontSize: 13, color: "#6b7280" }}>
        Loading SMS templates…
      </div>
    );
  }

  const campaignsToRender = campaignKey
    ? CAMPAIGNS.filter((c) => c.key === campaignKey)
    : CAMPAIGNS;
  // When scoped to a single campaign the per-section header in Settings already
  // labels the campaign, so we hide our own "SMS message templates" header and
  // per-campaign label/border to avoid double framing.
  const compact = !!campaignKey;

  return (
    <div style={compact ? {} : { marginTop: 22, paddingTop: 18, borderTop: "1px solid #f0f0f0" }}>
      {!compact && (
        <>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#374151", marginBottom: 4 }}>
            SMS message templates
          </div>
          <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 12px", lineHeight: 1.5 }}>
            Previews below show what a contact named Jane would receive. Each SMS part costs $0.20.
          </p>
        </>
      )}

      {loadError && (
        <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 10 }}>{loadError}</div>
      )}

      {campaignsToRender.map((c) => (
        <div key={c.key} style={compact
          ? { marginBottom: 4 }
          : { marginBottom: 12, padding: 12, border: "1px solid #f0f0f0", borderRadius: 10, background: "#fff" }}>
          {!compact && (
            <div style={{ fontWeight: 600, fontSize: 13, color: "#1f2937", marginBottom: 8 }}>
              {c.label}
            </div>
          )}
          {VARIANTS.map((v) => {
            const value = values[c.key]?.[v.key] || "";
            const preview = renderTemplate(value, previewVars);
            const seg = segmentInfo(preview);
            const cost = seg.parts * SMS_PART_COST_CENTS;
            const isCustomised = saved[c.key]?.[v.key];
            return (
              <div key={v.key} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {v.label}
                    </span>
                    {isCustomised && (
                      <span style={{ fontSize: 10, fontWeight: 500, color: "#0a9fa6", textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Customised
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditing({ campaignKey: c.key, variantKey: v.key })}
                    style={{
                      padding: "4px 10px", borderRadius: 6, border: "1px solid #d1d5db",
                      background: "#fff", color: "#374151", fontWeight: 600, fontSize: 11,
                      cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0,
                    }}
                  >
                    <FaPen size={9} /> Edit
                  </button>
                </div>
                <div style={{
                  padding: "8px 10px", background: "#f9fafb", border: "1px dashed #e5e7eb",
                  borderRadius: 6, fontSize: 12.5, color: "#374151", lineHeight: 1.4, whiteSpace: "pre-wrap",
                }}>
                  {preview || <span style={{ color: "#9ca3af" }}>(empty)</span>}
                </div>
                <div style={{ marginTop: 3, fontSize: 11, color: "#6b7280" }}>
                  <strong style={{ color: "#374151" }}>{seg.chars}</strong> chars ·{" "}
                  <strong style={{ color: "#374151" }}>{seg.parts}</strong> part{seg.parts === 1 ? "" : "s"} ·{" "}
                  <strong style={{ color: cost > SMS_PART_COST_CENTS ? "#b45309" : "#059669" }}>
                    ${(cost / 100).toFixed(2)}
                  </strong>{" "}
                  per send · {seg.encoding}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {editing && (
        <EditModal
          campaignKey={editing.campaignKey}
          variantKey={editing.variantKey}
          initialValue={values[editing.campaignKey]?.[editing.variantKey] || ""}
          defaults={defaults}
          variables={variables}
          saved={saved}
          previewVars={previewVars}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
